import React from 'react';
import { readableOnWhite } from '../utils/color';
import { useGameStore } from '../store/gameStore';
import { usePlayoffRecords } from '../hooks/usePlayoffRecords';
import { recordText } from '../sim/seasonRecord';
import { FEEDER_SIGNING_WEEK, getSeasonPhase, getTeamGameForWeek, LAST_REGULAR_SEASON_WEEK, LAST_TRAINING_CAMP_WEEK, SEASON_PHASE_LABELS, SeasonPhase, firstPlayoffWeek } from '../sim/scheduleEngine';

/** What happens in a week without a game. */
function weekNote(week: number, phase: SeasonPhase, isLastWeek: boolean, signingThisSeason: boolean, roundDescription?: string): string {
  switch (phase) {
    case 'SPRING_EVALUATION':
      // A new game's first season has no signing day: its rosters already hold this year's freshmen
      if (signingThisSeason && week < FEEDER_SIGNING_WEEK) return 'Final prospect visits before Prospect signing day';
      if (signingThisSeason && week === FEEDER_SIGNING_WEEK) return '✍️ Prospect signing day: prospects pick their school';
      return 'New student enrollment · College recruiting';
    case 'SUMMER_CAMP':
      return week === LAST_TRAINING_CAMP_WEEK ? '📋 Depth chart set for the season' : 'High intensity training';
    case 'STATE_PLAYOFFS':
      return roundDescription ?? 'Playoff round';
    case 'POST_SEASON':
      return '🎓 Seniors graduation and signing';
    case 'OFF_SEASON':
      return isLastWeek ? 'The new school year begins as the week ends' : '🔍 Prospect clinics, 7-on-7 nights and tryouts';
    default:
      return 'Bye week';
  }
}
import { findDistrict, playoffRoundCount, seasonLength } from '../sim/league';
import { leagueRoundNames } from '../sim/playoffEngine';
import { rulesForState } from '../sim/stateRules';
import { Team } from '../types/game';
import { rivalryName } from '../data/rivalries';
import { specialNightLabel, specialNights } from '../sim/talkPicker';

export const ScheduleView: React.FC = () => {
  const { currentWeek, districtTeams, leagueTeams, league, seasonSchedule, interstateGames, playoffBracket, userTeamId, currentYear, feederClassYear, openTeamProfile, nationalLeagues } =
    useGameStore();
  const playoffs = usePlayoffRecords();
  const userTeam = districtTeams.find((t) => t.id === userTeamId);

  if (!userTeam) return null;

  const totalWeeks = league ? seasonLength(league) : 20;
  const playoffRounds = league ? playoffRoundCount(league) : 6;
  // The state's round names for the playoff weeks (Texas: Bi-District ... State Championship; Georgia: First Round ... State Championship)
  const { roundLabels: ROUND_LABELS, roundDescriptions: ROUND_DESCRIPTIONS } = rulesForState(league?.state).playoffs;
  const leagueRounds = league ? leagueRoundNames(league) : [];
  const districtName = league ? findDistrict(league, userTeamId)?.name : undefined;

  // Season built from the stored schedule, plus the user's playoff games from the bracket
  const schedule = Array.from({ length: totalWeeks }, (_, i) => {
    const weekNum = i + 1;
    const roundIndex = weekNum - firstPlayoffWeek(playoffRounds);
    const base = { week: weekNum, type: getSeasonPhase(weekNum), isCurrent: weekNum === currentWeek, isCompleted: weekNum < currentWeek };
    // A five-round state's open week before the playoffs
    if (weekNum > LAST_REGULAR_SEASON_WEEK && roundIndex < 0) return { ...base, label: 'Open week', opponent: null, isHome: false, result: null };

    if (roundIndex >= 0 && playoffBracket) {
      const node = playoffBracket.divisions
        .map((d) => d.rounds[roundIndex]?.find((n) => n.team1.id === userTeamId || n.team2.id === userTeamId))
        .find(Boolean);
      const isHome = node?.team1.id === userTeamId;
      const opponent: Team | null = node ? (isHome ? node.team2 : node.team1) : null;
      const userScore = isHome ? node?.team1Score : node?.team2Score;
      const opponentScore = isHome ? node?.team2Score : node?.team1Score;
      return {
        ...base,
        label: playoffBracket.roundNames[roundIndex] ? ROUND_LABELS[playoffBracket.roundNames[roundIndex]] : leagueRounds[roundIndex] && ROUND_LABELS[leagueRounds[roundIndex]],
        opponent,
        isHome,
        result: node?.winnerTeamId ? `${node.winnerTeamId === userTeamId ? 'W' : 'L'} ${userScore}-${opponentScore}` : null
      };
    }

    const scheduled = getTeamGameForWeek(seasonSchedule, weekNum, userTeamId);
    const isHome = scheduled?.homeTeamId === userTeamId;
    const opponentId = scheduled && (isHome ? scheduled.awayTeamId : scheduled.homeTeamId);
    const played = scheduled?.homeScore !== undefined;
    const userScore = isHome ? scheduled?.homeScore : scheduled?.awayScore;
    const opponentScore = isHome ? scheduled?.awayScore : scheduled?.homeScore;

    const opponent = leagueTeams.find((t) => t.id === opponentId) ?? nationalLeagues.flatMap((l) => l.teams).find((t) => t.id === opponentId) ?? null;
    // Homecoming and Senior Night (a rivalry game shows its own name, below)
    const night = scheduled && opponent ? specialNightLabel({ ...specialNights([...seasonSchedule, ...interstateGames], userTeamId, scheduled.gameId), rivalry: false }) : null;
    return {
      ...base,
      label: roundIndex >= 0 && roundIndex < playoffRounds ? ROUND_LABELS[leagueRounds[roundIndex]] : (night ?? undefined),
      opponent,
      isHome,
      result: !played
        ? null
        : scheduled!.forfeitedByTeamId === userTeamId
          ? 'L (forfeit)'
          : scheduled!.forfeitedByTeamId
            ? 'W (forfeit)'
            : `${userScore! > opponentScore! ? 'W' : 'L'} ${userScore}-${opponentScore}`
    };
  });

  return (
    <div style={{ maxWidth: '850px', margin: '0 auto' }}>
      <h3 style={{ margin: '0 0 4px 0', fontSize: '17px' }}>{totalWeeks}-week schedule</h3>
      <p className="ui-muted" style={{ margin: '0 0 10px 0' }}>
        Pre season and Prospect signing day, training camp, non-{rulesForState(league?.state).districtLabel.toLowerCase()} tune-ups, the {districtName ?? rulesForState(league?.state).districtLabel.toLowerCase()} race, the state playoffs and
        the off season.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {schedule.map((game, i) => {
          // A rivalry game carries its name and a red edge
          const rivalry = game.opponent ? rivalryName(userTeam, game.opponent) : undefined;
          return (
          <React.Fragment key={game.week}>
            {/* A header where each phase begins */}
            {(i === 0 || schedule[i - 1].type !== game.type) && (
              <h4 style={{ margin: i === 0 ? '0' : '10px 0 0', fontSize: '13px', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {SEASON_PHASE_LABELS[game.type].replace('District', rulesForState(league?.state).districtLabel)}{' '}
                <span style={{ color: '#475569', fontWeight: 'normal' }}>
                  · {(() => {
                    const last = schedule.filter((g) => g.type === game.type).pop()!.week;
                    return last === game.week ? `Week ${game.week}` : `Weeks ${game.week}–${last}`;
                  })()}
                </span>
              </h4>
            )}
            <div
              style={{
                padding: '10px 12px',
                background: game.isCurrent ? (rivalry ? '#FFF7F7' : '#EFF6FF') : '#fff',
                border: game.isCurrent ? `2px solid ${rivalry ? '#B91C1C' : '#3B82F6'}` : '1px solid #E2E8F0',
                ...(rivalry && { borderLeft: '5px solid #B91C1C' }),
                borderRadius: '10px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', flexWrap: 'nowrap' }}>
                <span style={{ fontSize: '14px' }}>
                  <strong>Week {game.week}</strong>
                  {game.label && <span style={{ color: '#475569', fontSize: '13px' }}> · {game.label}</span>}
                  {rivalry && <span style={rivalryTag}>⚔️ {rivalry}</span>}
                </span>
                {game.result && <strong style={{ color: game.result.startsWith('W') ? '#059669' : '#DC2626', fontSize: '14px' }}>{game.result}</strong>}
              </div>
              {game.opponent ? (
                <div style={{ fontSize: '14px', marginTop: '2px' }}>
                  {game.isHome ? 'vs.' : 'at'}{' '}
                  <button onClick={() => openTeamProfile(game.opponent!.id)} style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer', minHeight: '40px', display: 'inline-flex', alignItems: 'center', textAlign: 'left' }}>
                    <strong style={{ color: readableOnWhite(game.opponent.primaryColor, game.opponent.secondaryColor), textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                      {game.opponent.name} {game.opponent.mascot}
                    </strong>
                  </button>
                  <div style={{ fontSize: '12px', color: '#475569' }}>
                    {recordText(game.opponent, playoffs)} · {game.opponent.schemeOffense.replace(/_/g, ' ')} offense
                  </div>
                </div>
              ) : (
                <div style={{ color: '#475569', fontSize: '13px', marginTop: '2px' }}>{weekNote(
                    game.week,
                    game.type,
                    game.week === totalWeeks,
                    currentYear >= feederClassYear,
                    // A five-round state's open week, then each round's line (rounds start in its first playoff week)
                    game.week > LAST_REGULAR_SEASON_WEEK && game.week < firstPlayoffWeek(playoffRounds)
                      ? 'No game: the playoff field is set and the first round is next week'
                      : leagueRounds[game.week - firstPlayoffWeek(playoffRounds)] && ROUND_DESCRIPTIONS[leagueRounds[game.week - firstPlayoffWeek(playoffRounds)]]
                  )}</div>
              )}
            </div>
          </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const rivalryTag: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: '6px',
  fontSize: '10.5px',
  fontWeight: 800,
  borderRadius: '999px',
  padding: '1px 7px',
  background: '#FEE2E2',
  color: '#7F1D1D',
  verticalAlign: '1px'
};
